import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import type { WithContext, Thing } from "schema-dts";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  article?: boolean;
  jsonLd?: WithContext<Thing> | WithContext<Thing>[];
  noindex?: boolean;
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  breadcrumbs?: Array<{ name: string; url: string }>;
  children?: React.ReactNode;
}

interface CourseSchemaProps {
  name: string;
  description: string;
  provider: string;
  url: string;
  image: string;
  price: number;
  duration: string;
}

interface ArticleSchemaProps {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  publisher: string;
}

// Organization and WebSite Schema
export function generateOrganizationSchema(): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "One80Labs",
    url: "https://one80labs.com",
    logo: {
      "@type": "ImageObject",
      url: "https://one80labs.com/logo.png",
      width: 200,
      height: 60,
    },
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Omaha",
      addressRegion: "Nebraska",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-402-915-3616",
      contactType: "customer service",
      areaServed: "US",
      availableLanguage: "English",
    },
    sameAs: [
      "https://www.linkedin.com/company/one80labs",
      "https://github.com/ndkramer",
      "https://www.instagram.com/one80labs/",
    ],
    founder: {
      "@type": "Person",
      name: "Nick Kramer",
      jobTitle: "Founder & CEO",
      sameAs: "https://www.linkedin.com/in/ndkramer",
    },
  };
}

// WebSite Schema with SearchAction
export function generateWebSiteSchema(): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "One80Labs",
    url: "https://one80labs.com",
    description: "AI Training & Fractional Project Management Services",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://one80labs.com/search?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
    publisher: {
      "@type": "Organization",
      name: "One80Labs",
      sameAs: "https://one80labs.com",
    },
  };
}

// LocalBusiness Schema for Omaha focus
export function generateLocalBusinessSchema(): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "One80Labs",
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    url: "https://one80labs.com",
    telephone: "+1-402-915-3616",
    email: "hello@one80labs.com",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Omaha",
      addressRegion: "Nebraska",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2565,
      longitude: -95.9345,
    },
    areaServed: [
      {
        "@type": "City",
        name: "Omaha",
        addressRegion: "Nebraska",
        addressCountry: "US",
      },
      {
        "@type": "City",
        name: "Council Bluffs",
        addressRegion: "Iowa",
        addressCountry: "US",
      },
      {
        "@type": "State",
        name: "Nebraska",
        addressCountry: "US",
      },
    ],
    serviceArea: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 41.2565,
        longitude: -95.9345,
      },
      geoRadius: "50000",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "AI Training & Project Management Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "AI Training",
            description: "ChatGPT, Gemini, and Microsoft 365 Copilot training",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Fractional Project Management",
            description: "Part-time project management services",
          },
        },
      ],
    },
    sameAs: [
      "https://www.linkedin.com/company/one80labs",
      "https://github.com/ndkramer",
      "https://www.instagram.com/one80labs/",
    ],
  };
}

// FAQPage Schema
export function generateFAQSchema(
  faqs: Array<{ question: string; answer: string }>,
): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

// BreadcrumbList Schema
export function generateBreadcrumbSchema(
  breadcrumbs: Array<{ name: string; url: string }>,
): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}

// BlogPosting Schema
export function generateBlogPostingSchema(props: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  url: string;
  articleSection?: string;
  keywords?: string[];
}): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: props.headline,
    description: props.description,
    image: props.image,
    datePublished: props.datePublished,
    dateModified: props.dateModified,
    author: {
      "@type": "Person",
      name: props.author,
      sameAs: "https://www.linkedin.com/in/ndkramer",
    },
    publisher: {
      "@type": "Organization",
      name: "One80Labs",
      logo: {
        "@type": "ImageObject",
        url: "https://one80labs.com/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": props.url,
    },
    articleSection: props.articleSection || "AI & Technology",
    keywords: props.keywords || ["AI", "technology", "Omaha", "Nebraska"],
  };
}

// Course Schema
export function generateCourseSchema(
  props: CourseSchemaProps,
): WithContext<any> {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: props.name,
    description: props.description,
    provider: {
      "@type": "Organization",
      name: props.provider,
      sameAs: "https://one80labs.com",
    },
    url: props.url,
    image: props.image,
    offers: {
      "@type": "Offer",
      price: props.price,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      validFrom: new Date().toISOString(),
    },
    timeRequired: props.duration,
    educationalLevel: "Beginner to Intermediate",
    teaches: [
      "AI implementation",
      "Workflow automation",
      "Business productivity",
      "Technology optimization",
    ],
  };
}

// HowTo Schema for Prompts
export function generateHowToSchema(props: {
  name: string;
  description: string;
  image: string;
  url: string;
  steps: Array<{ text: string; name?: string }>;
  totalTime?: string;
  author?: string;
}): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: props.name,
    description: props.description,
    image: props.image,
    url: props.url,
    totalTime: props.totalTime || "PT5M",
    author: {
      "@type": "Person",
      name: props.author || "One80Labs",
    },
    step: props.steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name || `Step ${index + 1}`,
      text: step.text,
    })),
  };
}

// ContactPage Schema
export function generateContactPageSchema(): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact One80Labs",
    description:
      "Get in touch for AI training and fractional project management services",
    url: "https://one80labs.com/contact",
    mainEntity: {
      "@type": "Organization",
      name: "One80Labs",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+1-402-915-3616",
        contactType: "customer service",
        areaServed: "US",
        availableLanguage: "English",
      },
    },
  };
}

// ItemList Schema for Blog Index
export function generateItemListSchema(
  items: Array<{ name: string; url: string; description?: string }>,
): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Blog Posts",
    description: "Latest AI and technology blog posts from One80Labs",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      url: item.url,
      description: item.description,
    })),
  };
}

export function generateArticleSchema(
  props: ArticleSchemaProps,
): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: props.headline,
    description: props.description,
    image: props.image,
    datePublished: props.datePublished,
    dateModified: props.dateModified,
    author: {
      "@type": "Person",
      name: props.author,
    },
    publisher: {
      "@type": "Organization",
      name: props.publisher,
      logo: {
        "@type": "ImageObject",
        url: "https://one80labs.com/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://one80labs.com",
    },
  };
}

// Enhanced schema for AI compliance and governance content
export function generateAIComplianceSchema(props: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  publisher: string;
  keywords: string[];
  geographicArea: string;
  industryFocus: string[];
}): WithContext<Thing>[] {
  const baseArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: props.headline,
    description: props.description,
    image: props.image,
    datePublished: props.datePublished,
    dateModified: props.dateModified,
    author: {
      "@type": "Person",
      name: props.author,
      jobTitle: "AI Compliance Specialist",
      worksFor: {
        "@type": "Organization",
        name: "One80Labs",
        url: "https://one80labs.com",
      },
    },
    publisher: {
      "@type": "Organization",
      name: props.publisher,
      logo: {
        "@type": "ImageObject",
        url: "https://one80labs.com/logo.png",
        width: 600,
        height: 60,
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: "4383 Nicholas Street #302",
        addressLocality: "Omaha",
        addressRegion: "NE",
        postalCode: "68181",
        addressCountry: "US",
      },
      areaServed: {
        "@type": "GeoCircle",
        geoMidpoint: {
          "@type": "GeoCoordinates",
          latitude: 41.2651,
          longitude: -95.9397,
        },
        geoRadius: "500",
        description:
          "Serving Omaha, Lincoln, Council Bluffs, and the greater Midwest region",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://one80labs.com",
    },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["article", ".blog-title", ".blog-meta", ".blog-content"],
    },
    isAccessibleForFree: "True",
    keywords: props.keywords.join(", "),
    about: [
      {
        "@type": "Thing",
        name: "AI Compliance",
        description:
          "Regulatory compliance for artificial intelligence systems",
      },
      {
        "@type": "Thing",
        name: "AI Governance",
        description: "Framework for responsible AI development and deployment",
      },
      {
        "@type": "Thing",
        name: "Responsible AI",
        description:
          "Ethical and responsible development of artificial intelligence",
      },
    ],
    audience: {
      "@type": "Audience",
      audienceType:
        "Business professionals, compliance officers, AI practitioners",
      geographicArea: {
        "@type": "Place",
        name: props.geographicArea,
      },
    },
    mentions: props.industryFocus.map((industry) => ({
      "@type": "Thing",
      name: industry,
    })),
  };

  // Additional schema for FAQ content
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is AI compliance and why is it important for Midwest businesses?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "AI compliance refers to adhering to regulatory requirements and ethical guidelines when implementing artificial intelligence systems. For Midwest businesses, it's crucial for maintaining trust, avoiding legal risks, and ensuring responsible AI adoption that aligns with regional business practices and regulations.",
        },
      },
      {
        "@type": "Question",
        name: "How can Omaha businesses implement responsible AI governance?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Omaha businesses can implement responsible AI governance by establishing clear policies, conducting regular audits, ensuring transparency in AI decision-making, and staying updated with evolving regulations. Our roadmap provides step-by-step guidance tailored to the Midwest business environment.",
        },
      },
      {
        "@type": "Question",
        name: "What are the key regulatory considerations for AI in the Midwest?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Key regulatory considerations include data privacy laws, industry-specific regulations, federal AI guidelines, and emerging state-level AI policies. Midwest businesses must also consider sector-specific requirements for healthcare, finance, and manufacturing industries.",
        },
      },
    ],
  };

  // Schema for the organization providing the content
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs - AI Compliance & Governance Experts",
    description:
      "Specialized AI compliance and governance consulting for Midwest businesses, providing roadmap development, policy creation, and implementation guidance for responsible AI adoption.",
    url: "https://one80labs.com",
    telephone: "+1-402-555-0123",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2651,
      longitude: -95.9397,
    },
    areaServed: [
      {
        "@type": "City",
        name: "Omaha",
      },
      {
        "@type": "City",
        name: "Lincoln",
      },
      {
        "@type": "City",
        name: "Council Bluffs",
      },
      {
        "@type": "State",
        name: "Nebraska",
      },
      {
        "@type": "State",
        name: "Iowa",
      },
    ],
    serviceArea: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 41.2651,
        longitude: -95.9397,
      },
      geoRadius: "500",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "AI Compliance Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "AI Compliance Roadmap Development",
            description:
              "Comprehensive roadmap for implementing AI compliance and governance frameworks",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Responsible AI Governance Consulting",
            description:
              "Expert guidance on establishing responsible AI governance policies and procedures",
          },
        },
      ],
    },
    knowsAbout: [
      "AI Compliance",
      "Responsible AI Governance",
      "Regulatory Compliance",
      "AI Ethics",
      "Data Privacy",
      "Risk Management",
      "Policy Development",
    ],
  };

  return [baseArticle, faqSchema, organizationSchema];
}

// Enhanced schema for Real Estate AI content
export function generateRealEstateAISchema(props: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  publisher: string;
  keywords: string[];
  geographicArea: string;
  realEstateFocus: string[];
  aiApplications: string[];
}): WithContext<Thing>[] {
  const baseArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: props.headline,
    description: props.description,
    image: props.image,
    datePublished: props.datePublished,
    dateModified: props.dateModified,
    author: {
      "@type": "Person",
      name: props.author,
      jobTitle: "AI Real Estate Specialist",
      worksFor: {
        "@type": "Organization",
        name: "One80Labs",
        url: "https://one80labs.com",
      },
    },
    publisher: {
      "@type": "Organization",
      name: props.publisher,
      logo: {
        "@type": "ImageObject",
        url: "https://one80labs.com/logo.png",
        width: 600,
        height: 60,
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: "4383 Nicholas Street #302",
        addressLocality: "Omaha",
        addressRegion: "NE",
        postalCode: "68181",
        addressCountry: "US",
      },
      areaServed: {
        "@type": "GeoCircle",
        geoMidpoint: {
          "@type": "GeoCoordinates",
          latitude: 41.2651,
          longitude: -95.9397,
        },
        geoRadius: "500",
        description:
          "Serving Omaha, Lincoln, Council Bluffs, and the greater Midwest region",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://one80labs.com/blog/ai-trends-midwest-real-estate-agents",
    },
    articleSection: "Real Estate Technology",
    keywords: props.keywords.join(", "),
    about: [
      {
        "@type": "Thing",
        name: "Artificial Intelligence in Real Estate",
        description: "AI applications for real estate professionals",
      },
      {
        "@type": "Thing",
        name: "Midwest Real Estate",
        description: "Real estate market in the Midwest United States",
      },
      {
        "@type": "Thing",
        name: "Omaha Real Estate",
        description: "Real estate market in Omaha, Nebraska",
      },
      {
        "@type": "Thing",
        name: "Council Bluffs Real Estate",
        description: "Real estate market in Council Bluffs, Iowa",
      },
    ],
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["article", ".blog-title", ".blog-meta"],
    },
    isAccessibleForFree: "True",
  };

  // Local Business schema for real estate focus
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs",
    description:
      "AI training and consulting for real estate professionals in Omaha and the Midwest",
    url: "https://one80labs.com",
    telephone: "+1-402-290-2645",
    email: "nick@one80labs.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2651,
      longitude: -95.9397,
    },
    areaServed: [
      {
        "@type": "City",
        name: "Omaha",
        addressRegion: "NE",
        addressCountry: "US",
      },
      {
        "@type": "City",
        name: "Council Bluffs",
        addressRegion: "IA",
        addressCountry: "US",
      },
      {
        "@type": "City",
        name: "Lincoln",
        addressRegion: "NE",
        addressCountry: "US",
      },
      {
        "@type": "Place",
        name: "Midwest United States",
        description: "Midwest region of the United States",
      },
    ],
    serviceArea: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 41.2651,
        longitude: -95.9397,
      },
      geoRadius: "500",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "AI Training Services for Real Estate",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "AI Training for Real Estate Agents",
            description:
              "Comprehensive AI training for real estate professionals",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Real Estate AI Consulting",
            description:
              "AI implementation consulting for real estate businesses",
          },
        },
      ],
    },
    sameAs: [
      "https://www.linkedin.com/company/one80labs",
      "https://www.facebook.com/one80labs",
    ],
  };

  // FAQ schema for common real estate AI questions
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How can AI help real estate agents in Omaha and Council Bluffs?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "AI can help real estate agents with automated listing descriptions, client communication, lead generation, document management, and market analysis. These tools save time and improve efficiency for real estate professionals in the Midwest.",
        },
      },
      {
        "@type": "Question",
        name: "What AI tools are most useful for Midwest real estate agents?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The most useful AI tools include ChatGPT for content creation, AI chatbots for 24/7 lead engagement, automated document management systems, AI-powered net sheet calculators, and voice-based negotiation practice tools.",
        },
      },
      {
        "@type": "Question",
        name: "How is AI transforming the real estate market in Nebraska and Iowa?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "AI is transforming the real estate market by automating routine tasks, improving client communication, enhancing market analysis, streamlining transactions, and providing better customer service for buyers and sellers in Omaha, Council Bluffs, and the broader Midwest region.",
        },
      },
    ],
  };

  return [baseArticle, localBusinessSchema, faqSchema];
}

export function generatePromptOptimizationSchema(props: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  publisher: string;
  keywords: string[];
  geographicArea: string;
  promptTechniques: string[];
  aiApplications: string[];
}): WithContext<Thing>[] {
  const baseArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: props.headline,
    description: props.description,
    image: props.image,
    datePublished: props.datePublished,
    dateModified: props.dateModified,
    author: {
      "@type": "Person",
      name: props.author,
    },
    publisher: {
      "@type": "Organization",
      name: props.publisher,
      logo: {
        "@type": "ImageObject",
        url: "https://one80labs.com/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://one80labs.com",
    },
    keywords: props.keywords.join(", "),
    articleSection: "AI Prompt Engineering",
    about: [
      {
        "@type": "Thing",
        name: "Prompt Engineering",
        description: "Advanced techniques for optimizing AI prompts",
      },
      {
        "@type": "Thing",
        name: "AI Productivity",
        description: "Improving productivity through better AI interactions",
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs",
    description:
      "AI training and prompt engineering expertise in Omaha and the Midwest",
    url: "https://one80labs.com",
    telephone: "+1-402-555-0123",
    address: {
      "@type": "PostalAddress",
      streetAddress: "123 Main Street",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68102",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 41.2565,
        longitude: -95.9345,
      },
      geoRadius: "50000",
    },
    areaServed: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 41.2565,
        longitude: -95.9345,
      },
      geoRadius: "100000",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "AI Training Services",
      itemListElement: props.promptTechniques.map((technique) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: technique,
          description: `Training in ${technique.toLowerCase()} for improved AI interactions`,
        },
      })),
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What are Level 2 prompts and how do they improve AI outcomes?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Level 2 prompts are advanced prompt engineering techniques that provide better context, clearer instructions, and more specific outputs. They result in sharper insights, better outcomes, and less frustration compared to basic prompts.",
        },
      },
      {
        "@type": "Question",
        name: "How can I learn advanced prompt engineering techniques?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "You can learn advanced prompt engineering through structured training programs, practice with real-world scenarios, and understanding AI model capabilities and limitations.",
        },
      },
      {
        "@type": "Question",
        name: "What are the most effective prompt optimization strategies?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Effective strategies include providing clear context, using specific examples, breaking complex tasks into steps, and iterating on prompts based on results.",
        },
      },
    ],
  };

  return [baseArticle, localBusinessSchema, faqSchema];
}

export function generateAIJobThreatSchema(props: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  publisher: string;
  keywords: string[];
  geographicArea: string;
  careerStrategies: string[];
  aiIntegrationMethods: string[];
}): WithContext<Thing>[] {
  const baseArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: props.headline,
    description: props.description,
    image: props.image,
    datePublished: props.datePublished,
    dateModified: props.dateModified,
    author: {
      "@type": "Person",
      name: props.author,
    },
    publisher: {
      "@type": "Organization",
      name: props.publisher,
      logo: {
        "@type": "ImageObject",
        url: "https://one80labs.com/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://one80labs.com",
    },
    keywords: props.keywords.join(", "),
    articleSection: "AI Career Development",
    about: [
      {
        "@type": "Thing",
        name: "AI Career Strategy",
        description: "Strategies for thriving in an AI-driven workplace",
      },
      {
        "@type": "Thing",
        name: "Future of Work",
        description: "Adapting to AI integration in the workplace",
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs",
    description:
      "AI career development and workplace integration consulting in Omaha and the Midwest",
    url: "https://one80labs.com",
    telephone: "+1-402-555-0123",
    address: {
      "@type": "PostalAddress",
      streetAddress: "123 Main Street",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68102",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 41.2565,
        longitude: -95.9345,
      },
      geoRadius: "50000",
    },
    areaServed: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 41.2565,
        longitude: -95.9345,
      },
      geoRadius: "100000",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "AI Career Services",
      itemListElement: props.careerStrategies.map((strategy) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: strategy,
          description: `Career development strategy: ${strategy.toLowerCase()}`,
        },
      })),
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Is AI really a threat to my job?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "AI is more of a tool that will transform jobs rather than completely replace them. The key is learning to work alongside AI and developing skills that complement AI capabilities.",
        },
      },
      {
        "@type": "Question",
        name: "How can I make AI my biggest asset at work?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "You can make AI your biggest asset by learning to use AI tools effectively, developing AI literacy, focusing on uniquely human skills like creativity and emotional intelligence, and becoming an AI advocate in your organization.",
        },
      },
      {
        "@type": "Question",
        name: "What skills should I develop to stay competitive in an AI-driven workplace?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Focus on developing skills that AI cannot easily replicate: critical thinking, creativity, emotional intelligence, complex problem-solving, and the ability to work effectively with AI tools.",
        },
      },
    ],
  };

  return [baseArticle, localBusinessSchema, faqSchema];
}

// Person Schema for Authors
export function generatePersonSchema(author: {
  name: string;
  jobTitle?: string;
  bio?: string;
  imageUrl?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  github?: string;
  instagram?: string;
  education?: string;
  yearsExperience?: {
    ai?: number;
    projectManagement?: number;
    softwareDevelopment?: number;
  };
  expertise?: string[];
}): WithContext<Thing> {
  const personSchema: WithContext<Thing> = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: author.name,
    jobTitle: author.jobTitle || "AI Consultant & Trainer",
    description:
      author.bio ||
      "AI Consultant & Trainer specializing in ChatGPT, Gemini, and Microsoft 365 Copilot training",
    email: author.email || "hello@one80labs.com",
    telephone: author.phone || "+1-402-915-3616",
    image:
      author.imageUrl ||
      "https://raw.githubusercontent.com/ndkramer/One80Labs-Site/main/public/images/team/nick.kramer.png",
    sameAs: [
      author.linkedin || "https://www.linkedin.com/in/nickolaskramer/",
      author.github || "https://github.com/ndkramer",
      author.instagram || "https://www.instagram.com/one80labs/",
    ].filter(Boolean),
    worksFor: {
      "@type": "Organization",
      name: "One80Labs",
      url: "https://one80labs.com",
    },
    knowsAbout: author.expertise || [
      "Artificial Intelligence",
      "Project Management",
      "Product Management",
      "Software Development",
      "ChatGPT",
      "Gemini",
      "Microsoft 365 Copilot",
      "Claude",
      "Automation",
      "Process Improvement",
      "Lean Software Development",
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Omaha",
      addressRegion: "Nebraska",
      addressCountry: "US",
    },
    alumniOf: {
      "@type": "CollegeOrUniversity",
      name: "Montana State University",
      description: "Degree in Business Administration and Information Systems",
    },
  };

  // Add experience information if provided
  if (author.yearsExperience) {
    const experience = [];
    if (author.yearsExperience.ai) {
      experience.push(
        `${author.yearsExperience.ai} years in Artificial Intelligence`,
      );
    }
    if (author.yearsExperience.projectManagement) {
      experience.push(
        `${author.yearsExperience.projectManagement} years in Project/Product Management`,
      );
    }
    if (author.yearsExperience.softwareDevelopment) {
      experience.push(
        `${author.yearsExperience.softwareDevelopment} years in Software Development`,
      );
    }

    if (experience.length > 0) {
      personSchema.description = `${personSchema.description} ${experience.join(", ")}.`;
    }
  }

  return personSchema;
}

export default function SEO({
  title = "One80Labs - AI Training & Fractional Project Management in Omaha, Nebraska",
  description = "Transform your business with AI-powered project management and training in Omaha and the Midwest. Expert services in ChatGPT, Gemini, and Microsoft 365 Copilot.",
  image = "https://images.unsplash.com/photo-1677442135968-6d89485dd1d1",
  article = false,
  jsonLd,
  noindex = false,
  keywords = [],
  author,
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  breadcrumbs,
  children,
}: SEOProps) {
  const currentUrl =
    typeof window !== "undefined"
      ? window.location.href
      : "https://one80labs.com";

  // Debug logging
  console.log("🔍 SEO Component Debug:", {
    title,
    description: description?.substring(0, 100) + "...",
    image,
    article,
    currentUrl,
    author,
    publishedTime,
    modifiedTime,
    section,
    tags,
    hasJsonLd: !!jsonLd,
  });

  // Force update meta tags as backup
  useEffect(() => {
    try {
      console.log("🔧 useEffect: Updating meta tags...");
      console.log("🔧 useEffect: Received title:", title);
      console.log("🔧 useEffect: Received description:", description);

      // Store original title for comparison
      const originalTitle = document.title;
      console.log("🔧 useEffect: Original document.title:", originalTitle);

      document.title = title;

      const metaDescription = document.querySelector(
        'meta[name="description"]',
      );
      if (metaDescription) {
        metaDescription.setAttribute("content", description);
      }

      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute("content", title);
      }

      const ogDescription = document.querySelector(
        'meta[property="og:description"]',
      );
      if (ogDescription) {
        ogDescription.setAttribute("content", description);
      }

      console.log("✅ useEffect: Meta tags updated successfully");
      console.log("🎯 Current document.title:", document.title);
      console.log("🎯 Expected title:", title);
      console.log(
        "🎯 Title changed from:",
        originalTitle,
        "to:",
        document.title,
      );
    } catch (error) {
      console.error("❌ useEffect: Error updating meta tags:", error);
    }
  }, [title, description]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      {author && <meta name="author" content={author} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="One80Labs" />
      <meta property="og:locale" content="en_US" />

      {/* Article-specific Open Graph tags */}
      {article && (
        <>
          {author && <meta property="article:author" content={author} />}
          {publishedTime && (
            <meta property="article:published_time" content={publishedTime} />
          )}
          {modifiedTime && (
            <meta property="article:modified_time" content={modifiedTime} />
          )}
          {section && <meta property="article:section" content={section} />}
          {tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured Data / JSON-LD */}
      {/* Global Organization and WebSite schemas */}
      <script type="application/ld+json">
        {JSON.stringify([
          generateOrganizationSchema(),
          generateWebSiteSchema(),
          generateLocalBusinessSchema(),
          ...(breadcrumbs ? [generateBreadcrumbSchema(breadcrumbs)] : []),
        ])}
      </script>

      {/* Page-specific JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>
      )}

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      <meta name="format-detection" content="telephone=no" />

      {/* Theme Color */}
      <meta name="theme-color" content="#F98B3D" />
      <meta name="msapplication-TileColor" content="#F98B3D" />

      {/* Force update meta tags using useEffect */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            (function() {
              try {
                // Force update meta tags for better SEO
                document.title = "${title.replace(/"/g, '\\"')}";
                
                const metaDescription = document.querySelector('meta[name="description"]');
                if (metaDescription) {
                  metaDescription.setAttribute('content', "${description.replace(/"/g, '\\"')}");
                }
                
                const ogTitle = document.querySelector('meta[property="og:title"]');
                if (ogTitle) {
                  ogTitle.setAttribute('content', "${title.replace(/"/g, '\\"')}");
                }
                
                const ogDescription = document.querySelector('meta[property="og:description"]');
                if (ogDescription) {
                  ogDescription.setAttribute('content', "${description.replace(/"/g, '\\"')}");
                }
                
                console.log("🔧 Forced meta tag update:", {
                  title: "${title.replace(/"/g, '\\"')}",
                  description: "${description.replace(/"/g, '\\"')}"
                });
              } catch (error) {
                console.error("❌ Error updating meta tags:", error);
              }
            })();
          `,
        }}
      />
    </Helmet>
  );
}
